import { useState } from 'react'
import styled from 'styled-components'
import { BodySmallMedium, Title1, Title6 } from '../../components/styles/Typography'
import { theme } from '../../theme/theme'
import { ComponentLayout } from '../../components/layouts/ComponentLayout'
import { Grid } from '../../components/Grid'
import { TextInput } from '../../components/inputs/Text'
import { TextArea } from '../../components/inputs/TextArea'
import { Radios } from '../../components/inputs/Radios'
import { SelectInput } from '../../components/inputs/Select'
import { Divider } from '@mui/material'
import { BooleanInput } from '../../components/inputs/Boolean'
import { Slider } from '../../components/inputs/Slider'
import { MultipleOptions } from '../../components/inputs/MultipleOptions'
import { MultipleOptionsSingleSelect } from '../../components/inputs/MultipleOptionsSingleSelect'
import { DateInput } from '../../components/inputs/DateInput'
import { QuestionOption } from '@estercare/ester-shared'

const OptionsMockData = [
  {
    id: '1',
    text: 'Option 1',
  },
  {
    id: '2',
    text: 'Option 2',
  },
  {
    id: '3',
    text: 'Option 3',
  },
  {
    id: 'negative_negative',
    text: 'Negative',
  },
]

export const Inputs = () => {
  const [textStandard, setTextStandard] = useState('This is a standard valid text input')
  const [textDisabled, setTextDisabled] = useState('This is a disabled text input')
  const [textInvalid, setTextInvalid] = useState('This is an invalid text input')
  const [radioOption, setRadioOption] = useState('1')
  const [selectOption, setSelectOption] = useState('1')
  const [selectedMultipleOptions, setSelectedMultipleOptions] = useState<QuestionOption[]>([OptionsMockData[0]!])
  const [selectedMultipleOptionsSingleSelect, setSelectedMultipleOptionsSingleSelect] = useState([
    OptionsMockData[0]?.id,
  ])
  const [sliderValue, setSliderValue] = useState<string>()

  const MultipleOptionsOnchange = ({ id, text }: QuestionOption) => {
    const selectedOption = { id, text }
    const isSelected = selectedMultipleOptions.some((opt) => opt.id === id)

    if (id.includes('_negative')) {
      setSelectedMultipleOptions(isSelected ? [] : [selectedOption])
      return
    }

    if (isSelected) {
      setSelectedMultipleOptions((prevOptions) => prevOptions.filter((opt) => opt.id !== id))
    } else {
      setSelectedMultipleOptions((prevOptions) => [
        ...prevOptions.filter((opt) => !opt.id.includes('_negative')),
        selectedOption,
      ])
    }
  }

  return (
    <ComponentLayout>
      <StyledTitle1>Inputs</StyledTitle1>
      <StyledTitle6>Text input</StyledTitle6>
      <StyledGrid $columns={2}>
        <TextInput label="Standard" value={textStandard} onChange={(e) => setTextStandard(e.target.value)} />
        <TextInput label="Disabled" value={textDisabled} onChange={(e) => setTextDisabled(e.target.value)} disabled />
        <TextInput
          label="Invalid"
          value={textInvalid}
          onChange={(e) => setTextInvalid(e.target.value)}
          error="You filled in something wrong"
        />
      </StyledGrid>
      <Divider />
      <StyledTitle6>Text area input</StyledTitle6>
      <StyledGrid $columns={2}>
        <TextArea label="Standard" />
        <TextArea label="Disabled" disabled />
      </StyledGrid>
      <Divider />
      <StyledTitle6>Select input</StyledTitle6>
      <StyledGrid $columns={2}>
        <SelectInput
          placeholder="Select an option"
          options={[
            { label: 'Option 1', value: '1' },
            { label: 'Option 2', value: '2' },
            { label: 'Option 3', value: '3' },
          ]}
          value={selectOption}
          onChange={setSelectOption}
        />
      </StyledGrid>
      <Divider />
      <StyledTitle6>Boolean input</StyledTitle6>
      <StyledGrid $columns={2}>
        <BooleanInput
          options={[
            { id: 'yes', text: 'Ja' },
            { id: 'no', text: 'Nej' },
          ]}
          answer={[]}
          setAnswer={() => {}}
        />
      </StyledGrid>
      <Divider />
      <StyledTitle6>Radios</StyledTitle6>
      <StyledGrid $columns={2}>
        <Radios
          defaultValue={radioOption}
          options={['Option 1', 'Option 2', 'Option 3']}
          setChange={setRadioOption}
          component="component"
        />
      </StyledGrid>
      <Divider />
      <StyledTitle6>Slider</StyledTitle6>

      <StyledGrid $columns={2}>
        <Slider
          minValue={0}
          maxValue={100}
          minLabel="0 - Min"
          maxLabel="100 - Max"
          disabled={false}
          onChange={(value) => {
            setSliderValue(value)
          }}
          answer={sliderValue}
        />
        <Slider
          minValue={0}
          maxValue={10}
          minLabel="disabled"
          maxLabel="disabled"
          disabled={true}
          onChange={() => {}}
        />
      </StyledGrid>
      <Divider />
      <StyledTitle6>Input with Checkboxes</StyledTitle6>

      <StyledGrid $columns={4}>
        <MultipleOptions
          onSelect={MultipleOptionsOnchange}
          options={OptionsMockData.map((option) => ({
            ...option,
            isChecked: selectedMultipleOptions.some((opt) => opt.id === option.id),
          }))}
        />
      </StyledGrid>
      <Divider />
      <StyledTitle6>Multiple options single select </StyledTitle6>
      <StyledGrid $columns={2}>
        <MultipleOptionsSingleSelect
          setAnswer={(selectedVal) => setSelectedMultipleOptionsSingleSelect(selectedVal)}
          options={OptionsMockData.slice(0, 3).map(({ id, text }) => {
            return {
              id,
              text,
              isSelected: selectedMultipleOptionsSingleSelect.includes(id),
            }
          })}
        />
      </StyledGrid>
      <Divider />
      <StyledTitle6>Date inputs </StyledTitle6>
      <StyledBodySmallMedium>
        The order of elements in the granularity array affects how the date picker behaves.
        <br />
        When using year, month, and day together:
        <Spacing />• If year is first (e.g., {`['year', 'month', 'day']`}) the picker assumes the current year and
        month, only prompting for day selection.
        <br />• If day is first (e.g., {`['year', 'month', 'day']`}), the picker will prompt for all three values.
        <Spacing />
        Users can still manually change any date component regardless of the default behavior.
        <Spacing />
        When using only year and month, the order in granularity only affects the visual arrangement of inputs.
      </StyledBodySmallMedium>
      <StyledGrid $columns={4}>
        <DateContainer>
          <BodySmallMedium>{`Granualrity: ['day', 'month', 'year']`}</BodySmallMedium>
          <DateInput answer={null} onChange={() => {}} granularity={['day', 'month', 'year']} />
        </DateContainer>
        <DateContainer>
          <BodySmallMedium>{`Granualrity: ['year', 'month', 'day']`}</BodySmallMedium>
          <DateInput answer={null} onChange={() => {}} granularity={['year', 'month', 'day']} />
        </DateContainer>
        <DateContainer>
          <BodySmallMedium>{`Granualrity: ['month', 'year']`}</BodySmallMedium>
          <DateInput answer={null} onChange={() => {}} granularity={['month', 'year']} />
        </DateContainer>
        <DateContainer>
          <BodySmallMedium>{`Granualrity: ['year', 'month']`}</BodySmallMedium>
          <DateInput answer={null} onChange={() => {}} granularity={['year', 'month']} />
        </DateContainer>
        <DateContainer>
          <BodySmallMedium>{` Granualrity: ['year']`}</BodySmallMedium>
          <DateInput answer={null} onChange={() => {}} granularity={['year']} />
        </DateContainer>
      </StyledGrid>
    </ComponentLayout>
  )
}

const StyledBodySmallMedium = styled(BodySmallMedium)`
  margin-bottom: ${theme.spacing.large}px;
  color: ${theme.color.grey5};
`
const Spacing = styled.div`
  margin-bottom: ${theme.spacing.medium}px;
`
const DateContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.small}px;
`
const StyledTitle1 = styled(Title1)`
  margin-bottom: ${theme.spacing.large}px;
`
const StyledTitle6 = styled(Title6)`
  margin-top: ${theme.spacing.medium}px;
  margin-bottom: ${theme.spacing.medium}px;
  text-transform: uppercase;
`
const StyledGrid = styled(Grid)`
  gap: ${theme.spacing.large}px;
  margin-bottom: ${theme.spacing.large}px;
`
